import { useQuery } from "@tanstack/react-query"

import { api, ErrorApi } from "@/api/cliente"
import { Exportar } from "@/componentes/exportar/Exportar"
import type { DocumentoExportable, Seccion } from "@/componentes/exportar/modelo"
import { monto } from "@/componentes/tesoreria/dinero"
import { Cajon } from "@/componentes/ui/Cajon"
import { Insignia, type Tono } from "@/componentes/ui/Insignia"
import { Cabecera, Cuerpo, Fila, Tabla, Td, Th } from "@/componentes/ui/Tabla"
import { comoFecha } from "@/lib/fechas"

/** Lo que la ficha necesita de la CxC: lo trae ya la bandeja. */
export type CxcDeFicha = {
  id: number
  cliente: string
  concepto: string
  expediente_numero: string | null
  monto: string
  monto_cobrado: string
  moneda: string
  estado: string
  fecha_emision: string | null
  fecha_vencimiento: string | null
  /** Sólo en las espejo del 2.0. */
  sistema2_clave: string | null
  insignia?: { texto: string; tono: Tono }
}

type Rubro = {
  id: number
  tipo: string
  concepto: string
  rubro_tipo: string | null
  cantidad: string | null
  monto: string
  cobrado: string
  moneda: string
  estado: string
  fecha_cobro: string | null
}

type Detalle = {
  expediente: string
  estatus_operativo: string | null
  estado: string | null
  fecha_apertura: string | null
  cliente: string | null
  rubros: Rubro[]
}

const TIPO: Record<string, string> = {
  LIQUIDACION: "Servicio",
  GASTO_FINANCIADO: "Gasto financiado",
}

const ESTADO: Record<string, Tono> = { COBRADO: "exito", PENDIENTE: "aviso", ANULADO: "peligro" }

function centimos(texto: string): number {
  return Math.round(Number(texto) * 100)
}

/**
 * La ficha de una cuenta por cobrar (16/10): al pulsar el expediente en «Por cobrar».
 *
 * Arriba lo del 3.0 —monto, lo cobrado AQUÍ y el saldo—; debajo, si es espejo de un
 * expediente del 2.0, **los servicios y gastos financiados que la componen allí**, leídos
 * del 2.0 al abrirla (`GET /cxc/{id}/sistema2`) para que digan lo de hoy. Lo cobrado en
 * el 2.0 se enseña tal cual y no toca el cobro de aquí: son dos gestiones separadas.
 */
export function FichaCxc({ cxc, onCerrar }: { cxc: CxcDeFicha; onCerrar: () => void }) {
  const espejo = cxc.sistema2_clave !== null
  const detalle = useQuery({
    queryKey: ["cxc-sistema2", cxc.id],
    queryFn: () => api.get<Detalle>(`/api/v1/cxc/${cxc.id}/sistema2`),
    enabled: espejo,
    retry: false,
  })
  const saldo = (centimos(cxc.monto) - centimos(cxc.monto_cobrado)) / 100
  const vivos = (detalle.data?.rubros ?? []).filter((r) => r.estado !== "ANULADO")
  const total2 = vivos.reduce((s, r) => s + centimos(r.monto), 0) / 100
  const cobrado2 = vivos.reduce((s, r) => s + centimos(r.cobrado), 0) / 100
  const titulo = cxc.expediente_numero ? `EXP ${cxc.expediente_numero}` : `CxC ${cxc.id}`
  const subtitulo = `${cxc.cliente} · ${cxc.concepto}`

  const documento = (): DocumentoExportable => {
    const secciones: Seccion[] = [
      {
        titulo: "Cuenta por cobrar",
        columnas: [
          { titulo: "Cliente", tipo: "texto" },
          { titulo: "Concepto", tipo: "texto" },
          { titulo: "Estado", tipo: "texto" },
          { titulo: "Emisión", tipo: "fecha" },
          { titulo: "Vencimiento", tipo: "fecha" },
          { titulo: "Moneda", tipo: "texto" },
          { titulo: "Monto", tipo: "monto" },
          { titulo: "Cobrado aquí", tipo: "monto" },
          { titulo: "Saldo", tipo: "monto" },
        ],
        filas: [
          {
            celdas: [
              cxc.cliente,
              cxc.concepto,
              [cxc.estado, cxc.insignia?.texto].filter(Boolean).join(" · "),
              cxc.fecha_emision?.slice(0, 10) ?? null,
              cxc.fecha_vencimiento?.slice(0, 10) ?? null,
              cxc.moneda,
              Number(cxc.monto),
              Number(cxc.monto_cobrado),
              saldo,
            ],
          },
        ],
      },
    ]
    if (detalle.data && detalle.data.rubros.length > 0) {
      secciones.push({
        titulo: "Servicios en el 2.0",
        columnas: [
          { titulo: "Concepto", tipo: "texto" },
          { titulo: "Tipo", tipo: "texto" },
          { titulo: "Cantidad", tipo: "numero" },
          { titulo: "Moneda", tipo: "texto" },
          { titulo: "Monto", tipo: "monto" },
          { titulo: "Cobrado en el 2.0", tipo: "monto" },
          { titulo: "Estado", tipo: "texto" },
        ],
        filas: detalle.data.rubros.map((r) => ({
          celdas: [
            r.concepto,
            TIPO[r.tipo] ?? r.tipo,
            r.cantidad ? Number(r.cantidad) : null,
            r.moneda,
            Number(r.monto),
            Number(r.cobrado),
            r.estado.charAt(0) + r.estado.slice(1).toLowerCase(),
          ],
        })),
      })
    }
    return { titulo, subtitulo, secciones }
  }

  return (
    <Cajon abierto titulo={titulo} onCerrar={onCerrar} ancho="amplio">
      <div className="space-y-5 text-sm">
        <div className="flex flex-wrap justify-end gap-2">
          <Exportar titulo={titulo} subtitulo={subtitulo} documento={documento} />
        </div>
        <div className="space-y-1">
          <p className="font-semibold">{cxc.cliente}</p>
          <p style={{ color: "var(--fg-muted)" }}>{cxc.concepto}</p>
          {cxc.insignia && <Insignia tono={cxc.insignia.tono}>{cxc.insignia.texto}</Insignia>}
        </div>

        <dl className="grid grid-cols-3 gap-3">
          {[
            ["Monto", cxc.monto],
            ["Cobrado aquí", cxc.monto_cobrado],
            ["Saldo", saldo.toFixed(2)],
          ].map(([rotulo, valor]) => (
            <div key={rotulo} className="tarjeta p-3">
              <dt className="text-xs" style={{ color: "var(--fg-muted)" }}>
                {rotulo}
              </dt>
              <dd className="font-semibold tabular-nums">
                {monto(valor ?? "0")} {cxc.moneda}
              </dd>
            </div>
          ))}
        </dl>
        <p className="text-xs tabular-nums" style={{ color: "var(--fg-muted)" }}>
          Emitida {comoFecha(cxc.fecha_emision) || "—"} · vence{" "}
          {comoFecha(cxc.fecha_vencimiento) || "—"}
        </p>

        <section className="space-y-2">
          <h3 className="text-xs font-semibold uppercase" style={{ color: "var(--fg-muted)" }}>
            Servicios en el 2.0
          </h3>
          {!espejo ? (
            <p style={{ color: "var(--fg-muted)" }}>
              Esta cuenta se creó a mano en el 3.0: no tiene detalle en el 2.0.
            </p>
          ) : detalle.isPending ? (
            <p style={{ color: "var(--fg-muted)" }}>Leyendo el 2.0…</p>
          ) : detalle.isError ? (
            <p role="alert" style={{ color: "var(--destructive)" }}>
              {detalle.error instanceof ErrorApi
                ? detalle.error.message
                : "No se pudo leer el detalle del 2.0."}
            </p>
          ) : detalle.data.rubros.length === 0 ? (
            <p style={{ color: "var(--fg-muted)" }}>El 2.0 no tiene cuentas de este expediente.</p>
          ) : (
            <>
              {detalle.data.estatus_operativo && (
                <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
                  Expediente {detalle.data.estado === "CERRADO" ? "cerrado" : "abierto"} ·{" "}
                  {detalle.data.estatus_operativo.replaceAll("_", " ").toLowerCase()}
                </p>
              )}
              <Tabla>
                <Cabecera>
                  <Th>Concepto</Th>
                  <Th derecha>Cant.</Th>
                  <Th derecha>Monto</Th>
                  <Th derecha>Cobrado en el 2.0</Th>
                  <Th>Estado</Th>
                </Cabecera>
                <Cuerpo>
                  {detalle.data.rubros.map((r) => {
                    const anulado = r.estado === "ANULADO"
                    return (
                      <Fila key={r.id} data-testid={`rubro-${r.id}`}>
                        <Td>
                          <span className={anulado ? "line-through" : undefined}>{r.concepto}</span>
                          <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
                            {TIPO[r.tipo] ?? r.tipo}
                          </span>
                        </Td>
                        <Td numero derecha>
                          {r.cantidad ? Number(r.cantidad).toLocaleString("es-VE") : "—"}
                        </Td>
                        <Td numero derecha>
                          {monto(r.monto)} {r.moneda}
                        </Td>
                        <Td numero derecha>
                          {monto(r.cobrado)} {r.moneda}
                        </Td>
                        <Td>
                          <Insignia tono={ESTADO[r.estado] ?? "neutro"}>
                            {r.estado.charAt(0) + r.estado.slice(1).toLowerCase()}
                          </Insignia>
                        </Td>
                      </Fila>
                    )
                  })}
                </Cuerpo>
              </Tabla>
              <p className="text-xs tabular-nums" style={{ color: "var(--fg-muted)" }}>
                En el 2.0: {monto(total2.toFixed(2))} {cxc.moneda} en {vivos.length}{" "}
                {vivos.length === 1 ? "cuenta" : "cuentas"}, {monto(cobrado2.toFixed(2))} cobrados
                allí. Las anuladas salen tachadas y no suman.
              </p>
            </>
          )}
        </section>
      </div>
    </Cajon>
  )
}
